/**
 * Pure helpers for the send_email / send_email_with_template approval preview:
 * connection labels, and resolving the initially-selected connection + signature
 * from the fetched email context. Extracted from ToolApprovalCard so the card
 * stays a thin orchestrator and this logic is unit-testable.
 */

/** Connection shape from the email-send-context API */
export interface EmailConnection {
  connectionId: string;
  provider: string;
  providerConfigKey?: string;
  email?: string | null;
  isShared?: boolean;
}

interface SignatureLike {
  id?: string;
  name?: string;
  is_default?: boolean;
}

export function getProviderLabel(provider: string): string {
  return provider === 'google-mail' ? 'Gmail' : provider === 'outlook-oauth' ? 'Outlook' : provider;
}

export function isHubOutboundEmailTool(toolName: string): boolean {
  return toolName === 'send_email' || toolName === 'send_email_with_template';
}

/** Resolve display label for the email connection being used */
export function getSendEmailConnectionDisplay(
  input: { connectionId?: string; provider?: string },
  connections: EmailConnection[] | undefined
): { fromLabel: string; providerLabel: string; connectionId: string } {
  if (!connections?.length) {
    // The no-connections fallback still renders on the outbound-email approval
    // card, so it must use the SAME human-readable label as the resolved path —
    // showing the raw slug ('google-mail') is what the reader is checking
    // against when catching a send from the wrong mailbox.
    return {
      fromLabel: input.connectionId ? `Connection ${input.connectionId}` : 'Default connection',
      providerLabel: input.provider ? getProviderLabel(input.provider) : 'default',
      connectionId: input.connectionId || '—',
    };
  }
  const match =
    input.connectionId && input.provider
      ? connections.find(
          (c) =>
            c.connectionId === input.connectionId &&
            (c.provider === input.provider || c.providerConfigKey === input.provider)
        )
      : connections[0];
  const conn = match || connections[0];
  const providerLabel = getProviderLabel(conn.provider) || 'Email';
  const fromLabel = conn.email
    ? `${conn.email} (${providerLabel})`
    : `${conn.connectionId} (${providerLabel})`;
  return { fromLabel, providerLabel, connectionId: conn.connectionId };
}

/**
 * Which connection should be pre-selected when the email preview loads:
 * the AI-provided one, else the saved default, else the first connection.
 */
export function resolveInitialConnection(
  preview: any
): { connectionId: string; provider: string } | null {
  const connections = preview?.emailContext?.connections as EmailConnection[] | undefined;
  if (!preview?.input || !connections) return null;
  const input = preview.input as { connectionId?: string; provider?: string };
  const defaultPref = preview.emailContext.defaultPreference as
    | { connectionId?: string; provider?: string }
    | undefined;
  const effectiveConnectionId = input.connectionId ?? defaultPref?.connectionId;
  const effectiveProvider = input.provider ?? defaultPref?.provider;
  if (
    effectiveConnectionId &&
    effectiveProvider &&
    connections.some((c) => c.connectionId === effectiveConnectionId)
  ) {
    return { connectionId: effectiveConnectionId, provider: effectiveProvider };
  }
  if (connections.length > 0) {
    return { connectionId: connections[0].connectionId, provider: connections[0].provider };
  }
  return null;
}

/**
 * Which signature should be pre-selected for the chosen connection:
 * the AI-provided one (if valid), else the connection's default, else the first,
 * else none.
 */
export function resolveInitialSignature(
  preview: any,
  selectedConnectionId: string | null
): string | null {
  if (!preview?.input || !selectedConnectionId) return null;
  const signaturesByConnectionId = preview.emailContext?.signaturesByConnectionId as
    | Record<string, SignatureLike[]>
    | undefined;
  const signatures = signaturesByConnectionId?.[selectedConnectionId];
  if (!Array.isArray(signatures) || signatures.length === 0) return null;
  const input = preview.input as { signatureId?: string };
  const fromInput =
    input.signatureId && signatures.some((s) => s.id === input.signatureId)
      ? input.signatureId
      : null;
  const defaultSig = signatures.find((s) => s.is_default);
  return fromInput ?? defaultSig?.id ?? signatures[0]?.id ?? null;
}
